import has, { add as hasAdd } from '@dojo/framework/core/has';

import { lccstore } from 'lcc-state';
import { logSystem, send_messages } from 'logger';

import { svc } from './support/amsvc';
import { svc as permsvc } from './support/userinfo-svc';

/**
 * Get user's permissions, set as has values, and return as an array.
 *
 * @returns Promise<(string | null)[]>
 */
const permissions = async () => {
	const roles = await permsvc.roles();
	let nonLearnerRole = false;
	for (const rolename in roles) {
		hasAdd(`role-${rolename}`, roles[rolename]);
		nonLearnerRole = nonLearnerRole || (rolename !== 'Learner' && roles[rolename]);
	}
	hasAdd('non-learner-role', nonLearnerRole);
	return Object.keys(roles)
		.map((r) => (roles[r] ? r : null))
		.filter((r_1) => !!r_1);
};

const kickedOut = () => {
	window.location.replace('/');
};

/**
 * Check if user has permissions to access the site.
 *
 * @export
 * @returns Promise<boolean>
 */
export async function check_access(): Promise<boolean> {
	const { get, path } = lccstore;
	const sitestatus = get(path('sitestatus'));
	const { is_manager, is_support, is_learner, is_coordinator, is_reviewer } = sitestatus;

	hasAdd('access-learning-system', is_manager || is_support || is_learner || is_coordinator || is_reviewer);
	hasAdd('use-hints', is_manager || is_support || is_reviewer);

	if (!has('access-learning-system')) {
		logSystem.info('access denied -- no appropriate role');
		send_messages().then(kickedOut, kickedOut);
	}

	const [accessprods, perms] = await Promise.all([svc.products(), permissions()]);

	try {
		const { product, portal_host } = window.hcstatic;
		logSystem.info(() => `permissions: ${perms.join(', ')}`);
		// only products for this host
		const access = accessprods.filter((item) => item.url.includes(portal_host!));
		const productid = product ? product.toLowerCase() : undefined;
		const realsets = new Set<string>();

		let partner: { id: string; title: string } | undefined = undefined;

		// set up has entries for allowed products
		access.forEach((p) => {
			// if it exists, inject partner into hcstatic and set as body css class
			const { primary_partner_id, primary_partner_title } = p;
			if (primary_partner_id !== undefined) {
				partner = {
					id: primary_partner_id,
					title: primary_partner_title || ''
				};
				window.hcstatic.partner = partner;
				const body = document.body;
				body.classList.add(partner.id);
			}
			// add product id to has variables
			const prodid = p.product_id.toLowerCase();
			if (realsets.has(prodid)) {
				logSystem.warn(`${prodid} is defined multiple times in AM access data`);
			} else {
				hasAdd(prodid, true);
				realsets.add(prodid);
			}
		});
		if (productid && !has(productid)) {
			logSystem.warn(`${productid} was not listed in AM access data`);
			hasAdd(productid, true);
			realsets.add(productid);
		}
		return true;
	} catch (err) {
		logSystem.error('cannot load AM products data', err);
		try {
			await send_messages();
		} finally {
			kickedOut();
		}
		return false;
	}
}
